import { Group, Select, TextInput } from '@mantine/core';
import useWhoAmI from '@/hooks/useWhoAmI';
import EditUserFormBase from './EditUserFormBase';
import { UpdatePatientInput } from '@/lib/graphql/types';
import { isNotEmpty, useForm } from '@mantine/form';
import { usStateData } from '@/lib/data';
import { useEffect, useMemo } from 'react';

interface EditUserFormAddressesProps {
  label: string;
  onClose: () => void;
  index?: number;
}

const EditUserFormAddresses = ({
  label,
  onClose,
  index,
}: EditUserFormAddressesProps) => {
  const { user, isLoading } = useWhoAmI();

  const userAddresses = useMemo(
    () =>
      user?.addresses?.map(
        ({ __typename, ...restProperties }) => restProperties
      ) ?? [],
    [user]
  );

  const form = useForm<Pick<UpdatePatientInput, 'addresses'>>({
    initialValues: {
      addresses: userAddresses,
    },
    validate: {
      addresses: {
        line1: isNotEmpty('Please enter a street address'),
        city: isNotEmpty('Please enter a city'),
        state: isNotEmpty('Please select a state'),
        postalCode: (value) =>
          /^\d{5}$/g.test(value) ? null : 'Please enter your 5 digit zip code',
      },
    },
    validateInputOnBlur: true,
  });

  useEffect(() => {
    if (
      !isLoading &&
      !form.values.addresses?.length &&
      userAddresses.length > 0
    ) {
      form.setValues({
        addresses: userAddresses,
      });
    }
  }, [userAddresses, form, isLoading]);

  return (
    <EditUserFormBase form={form} onClose={onClose}>
      <TextInput
        placeholder={label}
        {...form.getInputProps(`addresses.${index}.line1`)}
        value={form.getInputProps(`addresses.${index}.line1`).value || ''}
      />
      <TextInput
        placeholder="Suite, apartment, etc. (optional)"
        {...form.getInputProps(`addresses.${index}.line2`)}
        value={form.getInputProps(`addresses.${index}.line2`).value || ''}
      />
      <TextInput
        placeholder="City"
        {...form.getInputProps(`addresses.${index}.city`)}
        value={form.getInputProps(`addresses.${index}.city`).value || ''}
      />
      <Group wrap="nowrap">
        <Select
          name="State"
          placeholder="State"
          data={usStateData}
          {...form.getInputProps(`addresses.${index}.state`)}
        />
        <TextInput
          placeholder="Zip code"
          {...form.getInputProps(`addresses.${index}.postalCode`)}
          value={
            form.getInputProps(`addresses.${index}.postalCode`).value || ''
          }
        />
      </Group>
    </EditUserFormBase>
  );
};

export default EditUserFormAddresses;
